import AppFormContainer from "@/components/AppFormContainer";
import ResultPanel from "@/components/comm-generator/ResultPanel";
import SideTitle from "@/components/SideTitle";
import SplitPane from "@/components/SplitPane";
import FloatingDocStatuses from "@/components/results/FloatingDocStatuses";
import { buildDocStatuses } from "@/components/results/buildDocStatuses";
import TutorialBox, { useTutorialState } from "@/components/TutorialBox";
import { GuidedTour, TourStep } from "@/components/GuidedTour";
import { SubjectInput } from "@/components/lesson-lab/SubjectInput";
import { LessonTypesSection } from "@/components/lesson-lab/LessonTypesSection";
import { LanguageSelector } from "@/components/lesson-lab/LanguageSelector";
import { AccessibilityOptionsToggle } from "@/components/lesson-lab/AccessibilityOptionsToggle";
import { Sparkle } from "@phosphor-icons/react";
import { memo, useCallback, useMemo, useState, useEffect } from "react";
import { useForm, useFormState } from "react-hook-form";
import { useToolSubmission } from "@/lib/queries/tools/useToolSubmission"; // legacy (non-SSE)
import { useToolSubmissionSSE } from "@/lib/queries/tools/useToolSubmissionSSE";
import { useTranslation } from "react-i18next";
import useSessionStorage from "@/lib/hooks/useSessionStorage";
import useUserSettings from "@/lib/queries/user/useUserSettings";
import { useStytchSession } from "@stytch/react";
import { useA11ySettings } from "@/lib/hooks/useA11ySettings";
import { resolveInitialToolLanguage } from "@/lib/utils/language";
import { DEFAULT_RESULT_LANGUAGE } from "@/lib/constants/languages";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AGE_GROUPS,
  LEARNING_REQUIREMENTS,
  LESSON_TYPES,
} from "@/lib/types/comms";
import TemplateUploadModal from "@/components/TemplateUploadModal";
import { useUserTemplates } from "@/lib/queries/user/useTemplates";
import useInit from "@/lib/queries/user/useInit";

type CCForm = {
  subject: string;
  type: Array<{
    name: string;
    duration: string;
    checked: boolean;
    selectedTemplate?: string;
  }>;
  requirements: {
    language: string;
  };
  age: string;
  learning_requirement: string;
  showAccessibilityOptions: "yes" | "no";
  files: any;
  fileUrls: string[];
};

function CatchupCoach() {
  const [showAllTypes, setShowAllTypes] = useState(false);

  const reorderedLessonTypes = useMemo(() => {
    const priorityOrder = ["Worksheet", "Factsheet", "Test"];
    const priority = new Set(priorityOrder);
    const prioritized: any[] = [];
    const others: any[] = [];
    for (const lt of LESSON_TYPES) {
      if (priority.has(lt.value)) prioritized.push(lt);
      else others.push(lt);
    }
    prioritized.sort((a, b) => {
      return priorityOrder.indexOf(a.value) - priorityOrder.indexOf(b.value);
    });
    return [...prioritized, ...others];
  }, []);

  const { t } = useTranslation(["catchupCoach", "common"]);
  const isMobile = useIsMobile();
  const { session, fromCache } = useStytchSession();
  const { useGetUserSettings } = useUserSettings();
  const { data: userSettings } = useGetUserSettings({
    enabled: !!session && !fromCache,
  });
  const { language: a11yLanguage } = useA11ySettings();
  const initialLanguage = useMemo(
    () =>
      resolveInitialToolLanguage({
        a11yLanguage,
        userSettings,
        fallback: DEFAULT_RESULT_LANGUAGE,
      }),
    [a11yLanguage, userSettings]
  );
  const form = useForm<CCForm>({
    defaultValues: {
      subject: "",
      type: reorderedLessonTypes.map((type) => {
        const isWorksheet = type.value === "Worksheet";
        const defaultDuration = isWorksheet ? "30 minutes" : type.durations[0];
        return {
          name: type.value,
          duration: defaultDuration,
          checked: isWorksheet,
          selectedTemplate: "__no_template__",
        };
      }),
      requirements: { language: initialLanguage },
      age: AGE_GROUPS.UNSET,
      learning_requirement: LEARNING_REQUIREMENTS.UNSET,
      showAccessibilityOptions: "no",
      files: null,
      fileUrls: [],
    },
    resolver: (values: CCForm) => {
      const errors: any = {};
      const atLeastOneChecked = (values.type || []).some((i) => i.checked);
      if (!atLeastOneChecked) {
        errors.type = {
          message: t("materials.error", { ns: "lessonLab" }),
          type: "manual",
        };
      }
      return { values, errors } as any;
    },
  });
  const USE_SSE = true;
  const sse = USE_SSE ? useToolSubmissionSSE("catchupcoach") : (null as any);
  const mutation = !USE_SSE ? useToolSubmission("catchupcoach") : (null as any);
  const isPending = USE_SSE ? sse.isStreaming : Boolean(mutation?.isPending);
  const isSuccess = USE_SSE
    ? Boolean(sse.final || sse.latestOutput)
    : Boolean(mutation?.data);
  const data = USE_SSE ? sse.final : mutation?.data;
  const [storedIntermediate, setStoredIntermediate] = useState<string | null>(
    null
  );
  const { data: initData } = useInit();

  const [templateUploadModalOpen, setTemplateUploadModalOpen] = useState(false);
  const isTemplateFeatureEnabled = useMemo(
    () =>
      initData?.features?.some((f) => f.name === "Templates" && f.active) ??
      false,
    [initData]
  );
  const { data: templatesData } = useUserTemplates("Resource", {
    enabled: isTemplateFeatureEnabled,
  });
  const [lastSubject, setLastSubject] = useState<string | null>(null);
  const [savePreferencesEnabled, setSavePreferencesEnabled] = useState(false);
  const [getPreferences, setPreferences] = useSessionStorage(
    "catchup-coach-preferences",
    {}
  );
  // Save preferences
  useEffect(() => {
    const sub = form.watch((value) => {
      if (!value) return;
      if (form.formState.isDirty && savePreferencesEnabled) {
        setPreferences(value);
      }
    });
    return () => sub.unsubscribe();
  }, [form, savePreferencesEnabled, setPreferences]);
  // Load preferences
  useEffect(() => {
    if (getPreferences && Object.keys(getPreferences).length > 0) {
      form.reset({ ...form.getValues(), ...getPreferences });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Language sync
  useEffect(() => {
    const desired = resolveInitialToolLanguage({
      a11yLanguage,
      userSettings,
      fallback: DEFAULT_RESULT_LANGUAGE,
    });
    const current = form.getValues("requirements.language");
    const dirty = (form.formState.dirtyFields as any)?.requirements?.language;
    if (!dirty && current !== desired) {
      form.setValue("requirements.language", desired, { shouldDirty: false });
    }
  }, [a11yLanguage, userSettings, form, initialLanguage]);

  const getTemplatesForType = useMemo(() => {
    return (typeName: string) => {
      if (!templatesData?.templates) return [];
      return templatesData.templates.filter((template) =>
        template.tags.some((tag) => {
          if (tag.category === "resource" && tag.general && !tag.subcategory)
            return true;
          if (tag.category === "resource" && tag.tagText === typeName)
            return true;
          return false;
        })
      );
    };
  }, [templatesData?.templates]);

  const onSubmit = form.handleSubmit((values) => {
    const subjectChanged =
      lastSubject !== null && lastSubject !== values.subject;
    const intermediateValue = subjectChanged ? null : storedIntermediate;
    const selectedTypes = (values.type || []).filter((f) => f.checked);
    sse.start({
      subject: values.subject,
      type: selectedTypes.map((typeItem) => ({
        name: typeItem.name,
        recipient: "student",
        duration: parseInt(typeItem.duration),
        durationUnit: typeItem.duration.split(" ")[1],
        templateId:
          typeItem.selectedTemplate &&
          typeof typeItem.selectedTemplate === "string" &&
          typeItem.selectedTemplate !== "__no_template__"
            ? typeItem.selectedTemplate
            : undefined,
      })),
      requirements: {
        age:
          values.age === AGE_GROUPS.UNSET
            ? null
            : t(values.age, { lng: "en", ns: "common" }),
        language: t(values.requirements.language, { lng: "en", ns: "common" }),
        learning_requirement:
          values.learning_requirement === LEARNING_REQUIREMENTS.UNSET
            ? null
            : t(values.learning_requirement, { lng: "en", ns: "common" }),
      },
      regenerate: { intermediate: intermediateValue, feedback: "" },
      attachments: values.fileUrls,
    });
    setLastSubject(values.subject);
  });
  useEffect(() => {
    if (isSuccess && (data as any)?.intermediate) {
      setStoredIntermediate((data as any).intermediate);
    }
  }, [isSuccess, data]);

  const [tourOpen, setTourOpen] = useState(false);
  const tutorialState = useTutorialState("catchupcoach-tutorial");
  const tourSteps: TourStep[] = useMemo(
    () => [
      {
        selector: "#side-title",
        title: t("header.title"),
        content:
          t("tutorial.overview", {
            ns: "catchupCoach",
            defaultValue:
              "Plan catch-up materials by choosing content and options.",
          }) || "Plan catch-up materials by choosing content and options.",
      },
      {
        selector: "textarea[name=subject]",
        title: t("subject.label"),
        content:
          t("tutorial.subject", {
            ns: "catchupCoach",
            defaultValue: "Describe what students need to catch up on.",
          }) || "Describe what students need to catch up on.",
      },
      {
        selector: "[data-testid=lesson-types]",
        title: t("materials.label"),
        content:
          t("tutorial.materials", {
            ns: "catchupCoach",
            defaultValue: "Pick materials to generate.",
          }) || "Pick materials to generate.",
      },
      {
        selector: "#tour-language-container",
        title: t("common:language"),
        content:
          t("tutorial.language", {
            ns: "catchupCoach",
            defaultValue:
              "Select output language. Follows accessibility setting.",
          }) || "Select output language. Follows accessibility setting.",
      },
      {
        selector: "#tour-generate",
        title: t("common:submit.readyQuestion", { ns: "common" }),
        content:
          t("tutorial.generate", {
            ns: "catchupCoach",
            defaultValue: "Generate your catch-up materials.",
          }) || "Generate your catch-up materials.",
      },
    ],
    [t]
  );

  const getBadgeStyles = useCallback(() => "text-gray-700 bg-gray-100", []);
  const toggleShowAll = useCallback(() => setShowAllTypes((v) => !v), []);
  const noop = useCallback(() => {}, []);

  return (
    <>
      <SplitPane
        showResultPanel={
          USE_SSE
            ? Boolean(sse.final || sse.latestOutput)
            : Boolean(mutation?.data)
        }
        key={isSuccess ? "cc-result" : "cc-form"}
      >
        <AppFormContainer
          form={form}
          onSubmit={onSubmit}
          isSuccess={isSuccess}
          isPending={isPending}
          savePreferences={{
            enabled: savePreferencesEnabled,
            onToggle: setSavePreferencesEnabled,
          }}
        >
          <SideTitle
            title={t("header.title")}
            intro={t("header.intro")}
            icon={Sparkle}
            showHelpIndicator={tutorialState.dismissed}
            onHelpClick={tutorialState.reopen}
          />

          <TutorialBox
            storageKey="catchupcoach-tutorial"
            title={t("common:quickGuide", { defaultValue: "Quick guide" })}
            message={
              t("tutorial.blurb", {
                ns: "catchupCoach",
                defaultValue: "Take a quick tour of Catchup Coach.",
              }) || "Take a quick tour of Catchup Coach."
            }
            onStartTour={() => setTourOpen(true)}
            tutorialState={tutorialState}
          />
          {tourOpen && (
            <GuidedTour
              open
              steps={tourSteps}
              storageKey="catchupcoach-tutorial"
              onClose={() => setTourOpen(false)}
            />
          )}

          <SubjectInput
            control={form.control}
            onForceUpdate={() => {}}
            tool={"catchupCoach"}
          />

          <LessonTypesBlock
            control={form.control}
            setValue={form.setValue}
            watch={form.watch}
            lessonTypes={reorderedLessonTypes}
            showAllTypes={showAllTypes}
            onToggleShowAll={toggleShowAll}
            getTemplatesForType={getTemplatesForType}
            isTemplateFeatureEnabled={true}
            onTemplateUpload={noop}
            getBadgeStyles={getBadgeStyles}
            onTypeCheck={noop}
          />

          <LanguageSelector name="requirements.language" />
          <AccessibilityOptionsToggle
            control={form.control}
            watch={form.watch}
            t={t}
          />
        </AppFormContainer>

        <ResultPanel
          result={
            USE_SSE
              ? data?.results
                ? data.results
                : sse.latestOutput
                  ? [sse.latestOutput]
                  : undefined
              : mutation?.data?.results
          }
          appResult="catchup-coach"
          isLoading={isPending}
          toolResponse={USE_SSE ? data : mutation?.data}
          sseEvents={USE_SSE ? sse.events : undefined}
          sseError={USE_SSE ? sse.error : (mutation?.error as any)}
          loadingStatuses={
            USE_SSE
              ? buildDocStatuses({
                  expectedDocs: (form.getValues("type") || [])
                    .filter((t: any) => t.checked)
                    .map((tp: any) => ({
                      type: tp.name,
                      language:
                        (form.getValues("requirements.language") as string) ||
                        DEFAULT_RESULT_LANGUAGE,
                    })),
                  sseEvents: sse.events,
                  results:
                    (data?.results as any) ||
                    (sse.latestOutput ? [sse.latestOutput] : []),
                  isStreaming: sse.isStreaming,
                  isLoading: isPending,
                })
              : undefined
          }
          loadingStage={USE_SSE ? sse.stage : undefined}
          loadingProgress={USE_SSE ? sse.progress : undefined}
          onCancel={USE_SSE ? sse.abort : undefined}
        />
      </SplitPane>

      {USE_SSE &&
        (() => {
          const docStatuses = buildDocStatuses({
            expectedDocs: (form.getValues("type") || [])
              .filter((t: any) => t.checked)
              .map((tp: any) => ({
                type: tp.name,
                language:
                  (form.getValues("requirements.language") as string) ||
                  DEFAULT_RESULT_LANGUAGE,
              })),
            sseEvents: sse.events,
            results:
              (data?.results as any) ||
              (sse.latestOutput ? [sse.latestOutput] : []),
            isStreaming: sse.isStreaming,
            isLoading: isPending,
          });
          const hasReady = docStatuses.some((s) => s.status === "ready");
          const showFloating =
            Boolean(sse.isStreaming) &&
            (isMobile || hasReady) &&
            docStatuses.length > 0 &&
            sse.stage !== "done" &&
            !sse.error;
          return (
            <FloatingDocStatuses
              visible={showFloating}
              statuses={docStatuses}
              stage={sse.stage}
              progress={sse.progress}
              onClose={() => {}}
              onCancel={sse.abort}
            />
          );
        })()}
      {isTemplateFeatureEnabled && (
        <TemplateUploadModal
          open={templateUploadModalOpen}
          onOpenChange={setTemplateUploadModalOpen}
          onSuccess={() => {
            setTemplateUploadModalOpen(false);
          }}
        />
      )}
    </>
  );
}

export default CatchupCoach;

// A memoized block that subscribes only to 'type' field errors/changes
const LessonTypesBlock = memo(function LessonTypesBlock(props: {
  control: any;
  setValue: any;
  watch: any;
  lessonTypes: any[];
  showAllTypes: boolean;
  onToggleShowAll: () => void;
  getTemplatesForType: (typeName: string) => any[];
  isTemplateFeatureEnabled: boolean;
  onTemplateUpload: () => void;
  getBadgeStyles: (category?: string, subcategory?: string) => string;
  onTypeCheck?: (typeIndex: number, checked: boolean) => void;
}) {
  const { errors } = useFormState({ control: props.control, name: "type" });
  const typeError = (errors as any)?.type?.message;

  return (
    <LessonTypesSection
      control={props.control}
      setValue={props.setValue}
      lessonTypes={props.lessonTypes}
      showAllTypes={props.showAllTypes}
      onToggleShowAll={props.onToggleShowAll}
      typeError={typeError}
      getTemplatesForType={props.getTemplatesForType}
      isTemplateFeatureEnabled={props.isTemplateFeatureEnabled}
      onTemplateUpload={props.onTemplateUpload}
      getBadgeStyles={props.getBadgeStyles}
      onTypeCheck={props.onTypeCheck}
    />
  );
});
